import { Filter } from '../entities/Filter';
import { Product } from '../entities/Product';
import { FilterSet } from '../entities/FilterSet';
import { FilterQueryLogic } from '../../views/product-filters/IProductFilters';

export enum filterLogic {
    'AND',
    'OR'
}

const enum ArrayConditions {
    'EVERY' = 'every',
    'SOME' = 'some'
}

export class ProductFilterService {
    public filter(products: Product[], filterSets: FilterSet[]): Product[] {
        const orFilterSets = filterSets.filter(
            filterSet => filterSet.logic === FilterQueryLogic.OR
        );
        const andFilterSets = filterSets.filter(
            filterSet => filterSet.logic === FilterQueryLogic.AND
        );

        const filteredProducts = products.filter(product => {
            return (
                this.someFiltersArePassing(orFilterSets, product) &&
                this.allFiltersArePassing(andFilterSets, product)
            );
        });

        return filteredProducts;
    }

    public getAppliedFilters(appliedFiltersSet: FilterSet[]): Filter[] {
        return appliedFiltersSet.reduce((appliedFilters: Filter[], filterSet: FilterSet) => {
            return appliedFilters.concat(filterSet.activeFilters);
        }, []);
    }

    // create a Query String to reflect the applied filter state
    public getAppliedFiltersQueryString(appliedFiltersSet: FilterSet[]): string {
        let newUrl = '';
        // construct a new query string from the applied filters
        const urlParams = new URLSearchParams();
        // iterate over the applied filter sets
        appliedFiltersSet.forEach(filterSet => {
            // find the active filters from the set and extract their keys
            const activeFilterKeys = filterSet.activeFilters.map(filter => {
                return this.convertFilterValueLabel(filter.label);
            });
            // set one query string param per filter set
            urlParams.set(this.convertFilterKeyLabel(filterSet.key), activeFilterKeys.join(','));
        });
        // reconstruct URL
        const newParamStr = urlParams.toString();
        const separator = newParamStr.length > 0 ? '?' : '';
        newUrl = `${window.location.pathname}${separator}${unescape(newParamStr)}`;

        return newUrl;
    }

    // convert label for filter value into query param format
    // e.g. "Dry Skin" -> "dry-skin"
    public convertFilterValueLabel = (label: string): string => {
        let transformed = label.toLowerCase();
        transformed = transformed.trim();

        return transformed.replace(/ /g, '-');
    };

    // convert label for filter key into query param format
    // e.g. "skin_concern" -> "skin-concern"
    public convertFilterKeyLabel = (label: string): string => {
        return label.replace(/_/g, '-');
    };

    public static getAnalyticsAppliedFilters(appliedFilters: Filter[]) {
        return appliedFilters.map(Filter.toJson);
    }

    private checkFiltersUsingMethod(
        filterSets: FilterSet[],
        product: Product,
        method: ArrayConditions
    ) {
        if (!filterSets.length) {
            return true;
        }

        return filterSets[method](filterSet => filterSet.passes(product));
    }

    private someFiltersArePassing(filterSets: FilterSet[], product: Product) {
        return this.checkFiltersUsingMethod(filterSets, product, ArrayConditions.SOME);
    }

    private allFiltersArePassing(filterSets: FilterSet[], product: Product) {
        return this.checkFiltersUsingMethod(filterSets, product, ArrayConditions.EVERY);
    }
}
